/**
 * Map landmark catalog. Drop an SVG in `src/assets/map-landmarks/` and it
 * appears in the palette. Filenames become ids (`castle.svg` → `castle`).
 *
 * Artwork should be a single-color mark. Black fills/strokes are rewritten to
 * `currentColor` so the canvas ink and palette preview can tint independently.
 */

import { just, nothing, type Maybe } from "./maybe";

export type MapLandmarkIcon = {
  id: string;
  label: string;
  /** Source pixels at 1×; Nothing preserves the original legacy symbol size. */
  nativeSize: Maybe<{ width: number; height: number }>;
  /** View box for `paths`. Default `0 0 24 24`. */
  viewBox?: string;
  /** Stroke paths. Ignored when `svg` is set. */
  paths?: string[];
  /** Full SVG markup, typically from a raw SVG import. */
  svg?: string;
};

/** Palette order. Anything else in the folder is appended alphabetically. */
const LANDMARK_ORDER = [
  "town",
  "city",
  "castle",
  "citadel",
  "tower",
  "farm",
  "mountain",
  "trees",
  "water",
  "cloud",
  "ship",
  "flag",
  "banner",
  "sword",
  "swords",
  "axe",
  "axes",
  "bow",
  "trebuchet",
] as const;

const rawByPath = import.meta.glob("../assets/map-landmarks/**/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function idFromPath(path: string): string {
  const file = path.split("/").pop() ?? path;
  return file.replace(/\.svg$/i, "");
}

function labelFromId(id: string): string {
  const name = id
    .replace(/^(mercator|zannoni|harrewyn)-/, "")
    .replace(/^singleships-/, "ship-")
    .replace(/^shipgroups-/, "ship-group-")
    .replaceAll("-", " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function toCurrentColor(svg: string): string {
  return svg
    .replace(/#0{3,8}\b/gi, "currentColor")
    .replace(/#1[Cc]274[Cc]\b/g, "currentColor")
    .replace(/\bblack\b/g, "currentColor");
}

function nativeSize(svg: string): Maybe<{ width: number; height: number }> {
  const width = Number(svg.match(/<svg\b[^>]*\bwidth="([\d.]+)"/)?.[1]);
  const height = Number(svg.match(/<svg\b[^>]*\bheight="([\d.]+)"/)?.[1]);
  return Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0
    ? just({ width, height })
    : nothing;
}

const orderIndex = new Map<string, number>(
  LANDMARK_ORDER.map((id, index) => [id, index]),
);

const iconsByPath = Object.entries(rawByPath)
  .map(([path, svg]) => {
    const id = idFromPath(path);
    return {
      path,
      id,
      label: labelFromId(id),
      svg: toCurrentColor(svg),
      nativeSize: /\/(mercator|zannoni|harrewyn)\//.test(path)
        ? nativeSize(svg)
        : nothing,
    };
  })
  .sort((a, b) => {
    const ai = orderIndex.get(a.id) ?? LANDMARK_ORDER.length;
    const bi = orderIndex.get(b.id) ?? LANDMARK_ORDER.length;
    if (ai !== bi) return ai - bi;
    return a.label.localeCompare(b.label);
  });

/** Match specific subjects before broad settlement categories. */
const SUBJECTS: readonly (readonly [string, RegExp])[] = [
  ["Ships", /ship|boat/],
  ["Castles & forts", /castle|citadel|fort|redoubt|chateau/],
  ["Cities", /cit(?:y|ies)/],
  ["Towns", /town|village|hamlet/],
  ["Houses & farms", /house|farm|mansion|bastide|inn/],
  ["Towers", /tower/],
  ["Churches & abbeys", /chapel|church|abbey|mission|religious/],
  ["Mills", /mill/],
  ["Trees & forests", /tree|forest/],
  ["Mountains & hills", /mountain|hill/],
  ["Water & wetlands", /water|marsh|swamp|fountain/],
  ["Fields", /(^|-)fields?(-|$)/],
  ["Settlements", /settlement/],
  [
    "Map decorations",
    /compass|scale|cartouche|map-component|banner|flag|cloud/,
  ],
  ["Weapons & battlefields", /sword|axe|bow|trebuchet|battle/],
];

export const MAP_LANDMARK_GROUPS: {
  label: string;
  icons: MapLandmarkIcon[];
}[] = [
  "Towns",
  "Houses & farms",
  ...SUBJECTS.map(([label]) => label).filter(
    (label) => label !== "Towns" && label !== "Houses & farms",
  ),
  "Other symbols",
]
  .map((label) => ({
    label,
    icons: iconsByPath.filter((icon) => {
      const subject =
        SUBJECTS.find(([, pattern]) => pattern.test(icon.id))?.[0] ??
        "Other symbols";
      return subject === label;
    }),
  }))
  .filter((group) => group.icons.length > 0);

export function filterLandmarkGroups(
  query: string,
): typeof MAP_LANDMARK_GROUPS {
  const words = query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  return MAP_LANDMARK_GROUPS.map((group) => ({
    ...group,
    icons: group.icons.filter((icon) =>
      words.every((word) =>
        `${group.label} ${icon.label} ${icon.id}`.toLowerCase().includes(word),
      ),
    ),
  })).filter((group) => group.icons.length > 0);
}

export const MAP_LANDMARK_ICONS: MapLandmarkIcon[] =
  MAP_LANDMARK_GROUPS.flatMap((group) => group.icons);

const byId = new Map(MAP_LANDMARK_ICONS.map((icon) => [icon.id, icon]));

export function landmarkIconById(id: string): Maybe<MapLandmarkIcon> {
  const icon = byId.get(id);
  return typeof icon === "undefined" ? nothing : just(icon);
}
